/**
 * シグネチャ商品プロファイル 構造化編集画面。
 * 詳細設計: docs/design/v02-signature-product-profile-design.md §5-4
 *
 * AI対話式作成 (ProfileBuilderChatPage) とデータを共有する (同じ signature_product_profiles/components 行)。
 * 対話の途中経過をここで手動編集することもできるし、逆にここで作った内容をAI対話で補完することもできる。
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Pencil, Trash2, Save, X, ChefHat, AlertTriangle } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { ConfirmDialog, Modal } from '@/components/Modal';

type Store = { store_id: string; store_name: string; status: 'active' | 'closed' };
type Component = {
  component_id: string;
  component_type: string;
  component_name: string;
  attributes: Record<string, unknown>;
  notes: string | null;
  display_order: number;
};
type ProfileDetail = {
  profile_id: string;
  profile_type: string;
  profile_name: string;
  brand_concept: string | null;
  target_customer: string | null;
  allow_scope_1: boolean;
  allow_scope_2: boolean;
  allow_scope_3: boolean;
  status: 'draft' | 'active';
  stores: Array<{ store_id: string; store_name: string; is_primary: boolean }>;
  components: Component[];
};

const COMPONENT_TYPE_LABELS: Record<string, string> = {
  base_soup: 'ベーススープ',
  tare: 'かえし',
  aroma_oil: '香味油',
  noodle: '麺',
  topping_default: 'トッピング',
  other: 'その他',
};
const COMPONENT_TYPES = Object.keys(COMPONENT_TYPE_LABELS);

// デザインレビュー反映 (2026-08-02): 「AI対話を主導線にした理由 (専門用語を正確に言語化できない店舗担当者への配慮)」
// が空フォーム入力では活かされていなかったため、種類ごとによく使う属性項目をサジェストし、
// 想起ではなく再認で入力できるようにする (有識者提案のスーププロファイル項目例を参考)
const ATTRIBUTE_KEY_SUGGESTIONS: Record<string, string[]> = {
  base_soup: ['系統 (豚骨/鶏白湯/魚介 等)', 'スタイル (博多系/家系 等)', '主原料', '炊き方・炊き時間', '粘度', '脂質', '乳化度', '旨味の強さ', '香り', '後味', '避けたい食材・味'],
  tare: ['ベース (醤油/塩/味噌 等)', '塩味の強さ', '甘味の強さ', '香り'],
  aroma_oil: ['種類 (ラード/香味油 等)', '香りの強さ'],
  noodle: ['加水率', '太さ', '形状 (ストレート/縮れ 等)'],
  topping_default: ['具材名', '分量'],
  other: [],
};

type AttrRow = { key: string; value: string };
type ComponentForm = { component_type: string; component_name: string; notes: string; attrs: AttrRow[] };

const attrsToRows = (attrs: Record<string, unknown>): AttrRow[] =>
  Object.entries(attrs).map(([key, v]) => ({ key, value: Array.isArray(v) ? v.join('、') : String(v) }));
const rowsToAttrs = (rows: AttrRow[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const r of rows) {
    if (r.key.trim()) out[r.key.trim()] = r.value;
  }
  return out;
};

export function ProfileEditPage() {
  const { profileId } = useParams<{ profileId: string }>();
  const qc = useQueryClient();

  const { data: stores = [] } = useQuery<Store[]>({ queryKey: ['stores'], queryFn: () => apiFetch<Store[]>('/stores') });
  const { data: profile, isLoading } = useQuery<ProfileDetail>({
    queryKey: ['signature-profile', profileId],
    queryFn: () => apiFetch<ProfileDetail>(`/menu-dev/profiles/${profileId}`),
    enabled: Boolean(profileId),
  });

  const [basicForm, setBasicForm] = useState<{ profile_name: string; brand_concept: string; target_customer: string; allow_scope_1: boolean; allow_scope_2: boolean; allow_scope_3: boolean } | null>(null);
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[] | null>(null);
  const [componentModal, setComponentModal] = useState<{ mode: 'create' | 'edit'; componentId?: string; form: ComponentForm } | null>(null);
  const [deletingComponent, setDeletingComponent] = useState<Component | null>(null);

  const startEditingBasic = () => profile && setBasicForm({
    profile_name: profile.profile_name,
    brand_concept: profile.brand_concept ?? '',
    target_customer: profile.target_customer ?? '',
    allow_scope_1: profile.allow_scope_1,
    allow_scope_2: profile.allow_scope_2,
    allow_scope_3: profile.allow_scope_3,
  });
  const startEditingStores = () => profile && setSelectedStoreIds(profile.stores.map((s) => s.store_id));

  const saveBasicMut = useMutation({
    mutationFn: () => apiFetch(`/menu-dev/profiles/${profileId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        profile_name: basicForm!.profile_name.trim(),
        brand_concept: basicForm!.brand_concept.trim() || null,
        target_customer: basicForm!.target_customer.trim() || null,
        allow_scope_1: basicForm!.allow_scope_1,
        allow_scope_2: basicForm!.allow_scope_2,
        allow_scope_3: basicForm!.allow_scope_3,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-profile', profileId] });
      qc.invalidateQueries({ queryKey: ['signature-profiles'] });
      setBasicForm(null);
      toast.success('基本情報を更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const saveStoresMut = useMutation({
    mutationFn: () => apiFetch(`/menu-dev/profiles/${profileId}/stores`, {
      method: 'PUT',
      body: JSON.stringify({ store_ids: selectedStoreIds }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-profile', profileId] });
      qc.invalidateQueries({ queryKey: ['signature-profiles'] });
      setSelectedStoreIds(null);
      toast.success('適用店舗を更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const saveComponentMut = useMutation({
    mutationFn: () => {
      const body = {
        component_type: componentModal!.form.component_type,
        component_name: componentModal!.form.component_name.trim(),
        attributes: rowsToAttrs(componentModal!.form.attrs),
        notes: componentModal!.form.notes.trim() || undefined,
      };
      return componentModal!.mode === 'create'
        ? apiFetch(`/menu-dev/profiles/${profileId}/components`, { method: 'POST', body: JSON.stringify(body) })
        : apiFetch(`/menu-dev/profiles/${profileId}/components/${componentModal!.componentId}`, { method: 'PATCH', body: JSON.stringify(body) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-profile', profileId] });
      setComponentModal(null);
      toast.success('構成要素を保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const finalizeMut = useMutation({
    mutationFn: () => apiFetch(`/menu-dev/profiles/${profileId}`, { method: 'PATCH', body: JSON.stringify({ status: 'active' }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-profile', profileId] });
      qc.invalidateQueries({ queryKey: ['signature-profiles'] });
      toast.success('プロファイルを確定しました。AIメニュー開発で選択できるようになりました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '確定に失敗しました'),
  });

  const deleteComponentMut = useMutation({
    mutationFn: (componentId: string) => apiFetch(`/menu-dev/profiles/${profileId}/components/${componentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-profile', profileId] });
      setDeletingComponent(null);
      toast.success('構成要素を削除しました');
    },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeletingComponent(null); },
  });

  if (isLoading || !profile) {
    return <div className="text-sm text-neutral-sub">読込中…</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <Link to="/ai/menu-profiles" className="inline-flex items-center gap-1 text-sm text-neutral-sub hover:text-brand-red mb-2">
          <ArrowLeft size={14} /> プロファイル一覧へ戻る
        </Link>
        <h1 className="text-h1 flex items-center gap-2">
          <ChefHat size={22} /> {profile.profile_name}
          {profile.status === 'draft' && <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-warning-bg text-warning-text border border-warning-border">作成中 (下書き)</span>}
        </h1>
      </div>

      {/* 基本情報 */}
      <section className="p-5 rounded-xl border border-zinc-100 bg-white shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-h3">基本情報</h2>
          {!basicForm && <button type="button" onClick={startEditingBasic} className="p-1 text-brand-red hover:text-brand-red-hover" aria-label="編集"><Pencil size={15} /></button>}
        </div>
        {basicForm ? (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">プロファイル名*</label>
              <input value={basicForm.profile_name} onChange={(e) => setBasicForm({ ...basicForm, profile_name: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">ブランドコンセプト</label>
              <textarea value={basicForm.brand_concept} onChange={(e) => setBasicForm({ ...basicForm, brand_concept: e.target.value })} rows={2}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">想定顧客</label>
              <input value={basicForm.target_customer} onChange={(e) => setBasicForm({ ...basicForm, target_customer: e.target.value })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">許容する提案スコープ</label>
              <div className="space-y-1 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={basicForm.allow_scope_1} onChange={(e) => setBasicForm({ ...basicForm, allow_scope_1: e.target.checked })} /> 区分1: 既存スープ・仕込みを変更しない提案</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={basicForm.allow_scope_2} onChange={(e) => setBasicForm({ ...basicForm, allow_scope_2: e.target.checked })} /> 区分2: 提供直前の追加調味・トッピング等の調整</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={basicForm.allow_scope_3} onChange={(e) => setBasicForm({ ...basicForm, allow_scope_3: e.target.checked })} /> 区分3: スープの配合・製造工程自体の変更</label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setBasicForm(null)} className="px-3 py-1.5 rounded border border-neutral-line text-sm hover:bg-neutral-panel">キャンセル</button>
              <button type="button" disabled={saveBasicMut.isPending || !basicForm.profile_name.trim()} onClick={() => saveBasicMut.mutate()}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-brand-red text-white text-sm disabled:opacity-50"><Save size={14} /> 保存</button>
            </div>
          </div>
        ) : (
          <div className="text-sm space-y-1">
            {profile.brand_concept && <div><span className="text-neutral-sub">ブランドコンセプト：</span>{profile.brand_concept}</div>}
            {profile.target_customer && <div><span className="text-neutral-sub">想定顧客：</span>{profile.target_customer}</div>}
            <div className="text-neutral-sub">
              許容スコープ：
              {[profile.allow_scope_1 && '区分1', profile.allow_scope_2 && '区分2', profile.allow_scope_3 && '区分3'].filter(Boolean).join('、') || 'なし'}
            </div>
          </div>
        )}
      </section>

      {/* 適用店舗 */}
      <section className="p-5 rounded-xl border border-zinc-100 bg-white shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-h3">適用店舗</h2>
          {!selectedStoreIds && <button type="button" onClick={startEditingStores} className="p-1 text-brand-red hover:text-brand-red-hover" aria-label="編集"><Pencil size={15} /></button>}
        </div>
        {selectedStoreIds ? (
          <div className="space-y-3">
            <div className="space-y-1 max-h-40 overflow-y-auto border border-neutral-line rounded p-2">
              {stores.filter((s) => s.status === 'active').map((s) => (
                <label key={s.store_id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={selectedStoreIds.includes(s.store_id)}
                    onChange={() => setSelectedStoreIds(selectedStoreIds.includes(s.store_id) ? selectedStoreIds.filter((id) => id !== s.store_id) : [...selectedStoreIds, s.store_id])} />
                  {s.store_name}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setSelectedStoreIds(null)} className="px-3 py-1.5 rounded border border-neutral-line text-sm hover:bg-neutral-panel">キャンセル</button>
              <button type="button" disabled={saveStoresMut.isPending} onClick={() => saveStoresMut.mutate()}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-brand-red text-white text-sm disabled:opacity-50"><Save size={14} /> 保存</button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-neutral-text">{profile.stores.length > 0 ? profile.stores.map((s) => s.store_name).join('、') : '未紐付け'}</div>
        )}
      </section>

      {/* 構成要素 */}
      <section className="p-5 rounded-xl border border-zinc-100 bg-white shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-h3">構成要素</h2>
          <button type="button"
            onClick={() => setComponentModal({ mode: 'create', form: { component_type: 'base_soup', component_name: '', notes: '', attrs: [{ key: '', value: '' }] } })}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-neutral-line text-sm hover:bg-neutral-panel"
          ><Plus size={14} /> 追加</button>
        </div>
        {profile.components.length === 0 ? (
          <p className="text-sm text-neutral-sub">構成要素が登録されていません。ベーススープから登録することをお勧めします。</p>
        ) : (
          <div className="space-y-2">
            {profile.components.map((comp) => (
              <div key={comp.component_id} className="p-3 rounded border border-neutral-line">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">
                    <span className="text-xs text-neutral-sub mr-2">{COMPONENT_TYPE_LABELS[comp.component_type] ?? comp.component_type}</span>
                    {comp.component_name}
                  </div>
                  <div className="flex gap-1">
                    <button type="button" aria-label="編集"
                      onClick={() => setComponentModal({ mode: 'edit', componentId: comp.component_id, form: { component_type: comp.component_type, component_name: comp.component_name, notes: comp.notes ?? '', attrs: attrsToRows(comp.attributes).length > 0 ? attrsToRows(comp.attributes) : [{ key: '', value: '' }] } })}
                      className="p-1 text-brand-red hover:text-brand-red-hover"><Pencil size={13} /></button>
                    <button type="button" aria-label="削除" onClick={() => setDeletingComponent(comp)} className="p-1 text-neutral-sub hover:text-danger-text"><Trash2 size={13} /></button>
                  </div>
                </div>
                {Object.keys(comp.attributes).length > 0 && (
                  <div className="mt-2 text-xs text-neutral-sub space-y-0.5">
                    {Object.entries(comp.attributes).map(([k, v]) => (
                      <div key={k}>{k}: {Array.isArray(v) ? v.join('、') : String(v)}</div>
                    ))}
                  </div>
                )}
                {comp.notes && <div className="mt-1 text-xs text-neutral-sub">補足: {comp.notes}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      <Modal
        open={componentModal !== null}
        title={componentModal?.mode === 'create' ? '構成要素を追加' : '構成要素を編集'}
        onClose={() => setComponentModal(null)}
        footer={
          <>
            <button type="button" onClick={() => setComponentModal(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button type="button" disabled={saveComponentMut.isPending || !componentModal?.form.component_name.trim()} onClick={() => saveComponentMut.mutate()}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {saveComponentMut.isPending ? '保存中…' : '保存'}
            </button>
          </>
        }
      >
        {componentModal && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">種類*</label>
              <select value={componentModal.form.component_type}
                onChange={(e) => setComponentModal({ ...componentModal, form: { ...componentModal.form, component_type: e.target.value } })}
                className="w-full px-3 py-2 rounded border border-neutral-line"
              >
                {COMPONENT_TYPES.map((t) => <option key={t} value={t}>{COMPONENT_TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">名称*</label>
              <input autoFocus value={componentModal.form.component_name}
                onChange={(e) => setComponentModal({ ...componentModal, form: { ...componentModal.form, component_name: e.target.value } })}
                placeholder="例：博多系豚骨スープ"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">詳細属性 (自由項目)</label>
              <div className="space-y-2">
                {componentModal.form.attrs.map((row, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={row.key} placeholder="項目名 (例: 粘度)" aria-label="属性の項目名"
                      list="attribute-key-suggestions"
                      onChange={(e) => {
                        const attrs = [...componentModal.form.attrs];
                        attrs[i] = { ...attrs[i]!, key: e.target.value };
                        setComponentModal({ ...componentModal, form: { ...componentModal.form, attrs } });
                      }}
                      className="w-1/3 px-2 py-1.5 rounded border border-neutral-line text-sm" />
                    <input value={row.value} placeholder="内容 (例: 中濃度)" aria-label="属性の内容"
                      onChange={(e) => {
                        const attrs = [...componentModal.form.attrs];
                        attrs[i] = { ...attrs[i]!, value: e.target.value };
                        setComponentModal({ ...componentModal, form: { ...componentModal.form, attrs } });
                      }}
                      className="flex-1 px-2 py-1.5 rounded border border-neutral-line text-sm" />
                    <button type="button" aria-label="この項目を削除"
                      onClick={() => setComponentModal({ ...componentModal, form: { ...componentModal.form, attrs: componentModal.form.attrs.filter((_, idx) => idx !== i) } })}
                      className="p-1.5 text-neutral-sub hover:text-warning-text"><X size={14} /></button>
                  </div>
                ))}
                <datalist id="attribute-key-suggestions">
                  {(ATTRIBUTE_KEY_SUGGESTIONS[componentModal.form.component_type] ?? []).map((s) => <option key={s} value={s} />)}
                </datalist>
                <button type="button"
                  onClick={() => setComponentModal({ ...componentModal, form: { ...componentModal.form, attrs: [...componentModal.form.attrs, { key: '', value: '' }] } })}
                  className="inline-flex items-center gap-1 text-xs text-brand-red hover:text-brand-red-hover"><Plus size={12} /> 項目を追加</button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">補足</label>
              <textarea value={componentModal.form.notes}
                onChange={(e) => setComponentModal({ ...componentModal, form: { ...componentModal.form, notes: e.target.value } })}
                rows={2} className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={deletingComponent !== null}
        title="構成要素を削除しますか？"
        destructive
        confirmLabel="削除する"
        loading={deleteComponentMut.isPending}
        onClose={() => { if (!deleteComponentMut.isPending) setDeletingComponent(null); }}
        onConfirm={() => { if (deletingComponent) deleteComponentMut.mutate(deletingComponent.component_id); }}
        message={deletingComponent && <p>「{deletingComponent.component_name}」を削除します。この操作は取り消せません。</p>}
      />

      {profile.status === 'draft' && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-warning-bg border border-warning-border text-warning-text text-sm">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span className="flex-1">このプロファイルはまだ「下書き」状態のため、AIメニュー開発の提案には使用されません。</span>
          <button type="button" disabled={finalizeMut.isPending} onClick={() => finalizeMut.mutate()}
            className="shrink-0 px-3 py-1.5 rounded bg-brand-red text-white text-xs disabled:opacity-50">
            {finalizeMut.isPending ? '確定中…' : 'このプロファイルを確定する'}
          </button>
        </div>
      )}
    </div>
  );
}
